import { QRCodeSVG } from "qrcode.react";
import type { CardCanvasProps } from "./types";
import { DOMAIN_COLORS, DOMAIN_LABELS_AR } from "./constants";
import { getCardBg } from "./styleHelpers";
import CardLogo from "./CardLogo";
import WatermarkLogo from "./WatermarkLogo";
import CardDecorations from "./CardDecorations";
import TopDomainsBadges from "./TopDomainsBadges";
import { LinkedInStrengthRow } from "./StrengthRows";

/** LinkedIn banner card — Arabic / RTL (1200×630) */
export default function LinkedInCardRTL({
  userName,
  strengths,
  topDomains,
  platformUrl,
  profileUrl,
  cardRef,
  template,
}: CardCanvasProps) {
  const top10 = strengths.slice(0, 10);
  // Visual-left column = ranks 6-10, visual-right column = ranks 1-5
  const left5  = top10.slice(0, 5);
  const right5 = top10.slice(5, 10);

  return (
    <div
      ref={cardRef}
      style={{
        width: 1200,
        height: 630,
        background: getCardBg(template, "linkedin"),
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Cairo', 'Noto Sans Arabic', 'Tahoma', sans-serif",
        color: "#fff",
        direction: "rtl",
        flexShrink: 0,
      }}
    >
      <CardDecorations template={template} width={1200} height={630} isInstagram={false} />
      <WatermarkLogo />

      {/* Right info panel */}
      <div
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: 340,
          borderLeft: "1px solid rgba(255,255,255,0.08)",
          padding: "44px 36px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          direction: "rtl",
        }}
      >
        {/* Brand block */}
        <div>
          <div style={{ marginBottom: 28, display: "flex", justifyContent: "flex-start" }}>
            <CardLogo size={48} />
          </div>
          <div style={{ fontSize: 11, letterSpacing: 2, color: "rgba(255,255,255,0.45)", marginBottom: 8, textAlign: "right" }}>
            ملف كليفتون للقوة
          </div>
          <div style={{ fontSize: 26, fontWeight: template === "minimal" ? 600 : 800, lineHeight: 1.2, marginBottom: 6, textAlign: "right" }}>
            {userName}
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", textAlign: "right" }}>
            أفضل 10 نقاط قوة
          </div>
        </div>

        {/* Top domains */}
        <TopDomainsBadges topDomains={topDomains} isRTL={true} compact />

        {/* Domain legend */}
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: "rgba(255,255,255,0.35)", marginBottom: 10, textAlign: "right" }}>
            المجالات
          </div>
          {Object.entries(DOMAIN_COLORS).map(([domain, color]) => (
            <div key={domain} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7, justifyContent: "flex-start" }}>
              <div style={{ width: 9, height: 9, borderRadius: "50%", background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
                {DOMAIN_LABELS_AR[domain] ?? domain}
              </span>
            </div>
          ))}
        </div>

        {/* QR */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
          <div style={{ background: "#fff", padding: 8, borderRadius: 10 }}>
            <QRCodeSVG value={profileUrl} size={66} level="M" />
          </div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>استكشف المنصة</div>
        </div>
      </div>

      {/* Left panel — two explicit flex columns */}
      <div
        style={{
          position: "absolute",
          right: 340,
          top: 0,
          left: 0,
          bottom: 0,
          padding: "44px 36px",
          display: "flex",
          flexDirection: "row",
          gap: 16,
          alignItems: "center",
        }}
      >
        {/* visual-right = ranks 1-5 (first child = right in RTL flex) */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: template === "minimal" ? 0 : 10 }}>
          {left5.map((s) => <LinkedInStrengthRow key={s.rank} s={s} template={template} isRTL={true} />)}
        </div>
        {/* visual-left = ranks 6-10 */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: template === "minimal" ? 0 : 10 }}>
          {right5.map((s) => <LinkedInStrengthRow key={s.rank} s={s} template={template} isRTL={true} />)}
        </div>
      </div>
    </div>
  );
}
